import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import {
    CommentsPageResponse,
    CommentsQuery,
    useBlockComment,
    useCommentsList,
    useDestroyComment,
    usePinComment,
    useReactComment,
    useRestoreComment,
} from '../../hooks/useComments';
import { getImageUrl } from '../../lib/getImageSize';

import BasicButton from '../buttons/BasicButton';
import ReactionButton from '../buttons/ReactionButton';
import CommentForm from '../forms/CommentForm';
import PinnedIcon from '../icons/PinnedIcon';
import VerifiedIcon from '../icons/VerifiedIcon';
import Time from '../partials/Time';
import IdentifyModal from '../popups/IdentifyModal';

import styles from '../../styles/cards/comment-card.module.css';

import defaultAvatar from '../../assets/img/avatar.svg';

interface CommentCardProps {
    id?: string;
    author?: CommentAuthor;
    body?: string | null;
    createdAt?: string | null;
    isReply?: boolean;
    isDeleted?: boolean;
    isBlocked?: boolean;
    isPinned?: boolean;
    replyCount?: number;
    reactions?: {
        [key: string]: number;
    } | null;
    replies?: CommentsPageResponse | null;
    commentableKey: string | null;
    query?: CommentsQuery | null;
    onUpdated?: (comment: Comment) => void | null;
    placeholder?: boolean;
    className?: string | null;
}

function CommentCard({
    id = null,
    author,
    body,
    createdAt,
    isReply = false,
    isDeleted = false,
    isBlocked = false,
    isPinned = false,
    replyCount = 0,
    reactions = null,
    replies = null,
    commentableKey = null,
    query = null,
    onUpdated = null,
    placeholder = false,
    className = null,
}: CommentCardProps) {
    const url = useUrlGenerator();
    const user = useUser();
    const { role } = user || {};
    const isAdmin = role === 'admin' || role === 'staff';
    const tracking = useTracking();
    const { name, image, author: authorSnippet, verified = false } = author;
    const { slug: authorSlug = null } = authorSnippet || {};
    const thumbnailUrl = getImageUrl(image, 'thumbnail');
    const [repliesOpened, setRepliesOpened] = useState(replies !== null);
    const [replyOpened, setReplyOpened] = useState(false);
    const [loginModalOpened, setLoginModalOpened] = useState(false);
    const onClickReply = useCallback(() => {
        if (user === null) {
            setLoginModalOpened(true);
            return;
        }
        tracking.trackEvent('Comments', 'open_reply');
        setReplyOpened(!replyOpened);
        setRepliesOpened(true);
    }, [replyOpened, user, tracking]);
    const canReply = !isReply;
    const { reactAsync } = useReactComment(id);
    const { blockAsync } = useBlockComment(id);
    const { pinAsync } = usePinComment(id);
    const { destroyAsync } = useDestroyComment(id);
    const { restoreAsync } = useRestoreComment(id);
    const {
        items = [],
        isFetching = false,
        endIsReached = false,
        isLoading = false,
        fetchNextPage,
        addComment,
    } = useCommentsList(
        commentableKey,
        {
            parent: id,
            ...query,
        },
        {
            enabled: repliesOpened && canReply,
            // suspense: true,
            withOffset: true,
            ...(replies !== null
                ? {
                      initialData: {
                          pages: [replies],
                          pageParams: [replies.pagination.page],
                      },
                  }
                : {}),
        },
    );

    const onClickLoadMore = useCallback(() => {
        fetchNextPage();
    }, [fetchNextPage]);

    const onReplyComplete = useCallback(
        (newComment, parentComment = null) => {
            tracking.trackEvent('Comments', 'reply', null, null, {
                parent: parentComment,
                comment: newComment,
            });
            addComment(newComment);
            if (parentComment !== null && onUpdated !== null) {
                onUpdated(parentComment);
            }
        },
        [addComment, onUpdated],
    );

    const onReplyUpdated = useCallback(
        (newComment) => {
            addComment(newComment);
        },
        [addComment],
    );

    const onClickReaction = useCallback(() => {
        if (user === null) {
            setLoginModalOpened(true);
            return false;
        }
        return true;
    }, [user]);

    const onSelectReaction = useCallback(
        (newReaction) => {
            reactAsync(newReaction).then((updatedComment) => {
                tracking.trackEvent('Comments', 'reaction', null, null, {
                    reaction: newReaction,
                    comment: updatedComment,
                });
                if (onUpdated !== null) {
                    onUpdated(updatedComment);
                }
            });
        },
        [reactAsync, onUpdated, user, tracking],
    );

    const onLoginModalClosed = useCallback(() => {
        setLoginModalOpened(false);
    }, []);

    const onClickPin = useCallback(() => {
        pinAsync(true);
    }, [pinAsync]);

    const onClickUnpin = useCallback(() => {
        pinAsync(false);
    }, [pinAsync]);

    const onClickDelete = useCallback(() => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
            destroyAsync();
        }
    }, [destroyAsync]);

    const onClickRestore = useCallback(() => {
        if (window.confirm('Êtes-vous sûr de vouloir restaurer ce commentaire ?')) {
            restoreAsync();
        }
    }, [restoreAsync]);

    const onClickBlock = useCallback(() => {
        if (window.confirm('Êtes-vous sûr de vouloir bloquer cet utilisateur ?')) {
            blockAsync(true);
        }
    }, [blockAsync]);

    const onClickUnblock = useCallback(() => {
        if (window.confirm('Êtes-vous sûr de vouloir débloquer cet utilisateur ?')) {
            blockAsync(false);
        }
    }, [blockAsync]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isDeleted]: isDeleted,
                    [styles.isBlocked]: isBlocked,
                    [styles.isPinned]: isPinned,
                    [styles.isVerified]: verified,
                    [styles.isReply]: isReply,
                },
                className,
            ])}
        >
            <div className={styles.avatar}>
                <div className={styles.shape}>
                    <img src={thumbnailUrl || defaultAvatar} alt={name} className={styles.image} />
                </div>
                {verified ? <VerifiedIcon className={styles.verified} /> : null}
            </div>
            <div className={styles.comment}>
                <div className={styles.author}>
                    {isPinned ? <PinnedIcon className={styles.pinnedIcon} /> : null}
                    <span>
                        {authorSlug !== null ? (
                            <Link href={url('author', { slug: authorSlug })}>{name}</Link>
                        ) : (
                            name
                        )}
                    </span>
                    <small>
                        <Time date={createdAt} className={styles.date} />
                    </small>
                </div>
                <div className={styles.body} dangerouslySetInnerHTML={{ __html: body }} />
                <div className={styles.footer}>
                    <ReactionButton
                        onClick={onClickReaction}
                        onSelect={onSelectReaction}
                        className={classNames([
                            styles.tool,
                            { [styles.inactive]: reactions === null },
                            styles.reactionButton,
                        ])}
                    >
                        {reactions !== null ? (
                            <span className={styles.reactions}>
                                {Object.keys(reactions).map((reaction) => (
                                    <span className={styles.reaction} key={`reaction-${reaction}`}>
                                        <span className={styles.emoji}>{reaction}</span>
                                        <span className={styles.count}>{reactions[reaction]}</span>
                                    </span>
                                ))}
                            </span>
                        ) : null}
                    </ReactionButton>
                    {canReply ? (
                        replyCount > 0 && replies === null ? (
                            <BasicButton
                                onClick={onClickReply}
                                className={classNames([styles.tool, styles.replyButton])}
                            >
                                <FormattedMessage
                                    defaultMessage="{count} réponse(s)"
                                    values={{ count: replyCount }}
                                    description="Comment reply count"
                                />
                            </BasicButton>
                        ) : (
                            <BasicButton
                                onClick={onClickReply}
                                className={classNames([
                                    styles.tool,
                                    styles.inactive,
                                    styles.replyButton,
                                ])}
                            >
                                Répondre
                            </BasicButton>
                        )
                    ) : null}
                    {isAdmin ? (
                        <>
                            {isPinned ? (
                                <BasicButton
                                    onClick={onClickUnpin}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    Désépingler
                                </BasicButton>
                            ) : (
                                <BasicButton
                                    onClick={onClickPin}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    Épingler
                                </BasicButton>
                            )}
                            {isDeleted ? (
                                <BasicButton
                                    onClick={onClickRestore}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    Restaurer
                                </BasicButton>
                            ) : (
                                <BasicButton
                                    onClick={onClickDelete}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    Supprimer
                                </BasicButton>
                            )}
                            {isBlocked ? (
                                <BasicButton
                                    onClick={onClickUnblock}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    Débloquer
                                </BasicButton>
                            ) : (
                                <BasicButton
                                    onClick={onClickBlock}
                                    className={classNames([styles.tool, styles.admin])}
                                >
                                    Bloquer
                                </BasicButton>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
            {canReply && repliesOpened ? (
                <div className={styles.replies}>
                    {(items || []).map((item) => (
                        <CommentCard
                            key={`comment-${item.id}`}
                            {...item}
                            onUpdated={onReplyUpdated}
                            commentableKey={commentableKey}
                            query={query}
                            className={styles.reply}
                        />
                    ))}
                    {!endIsReached && !isLoading ? (
                        <BasicButton onClick={onClickLoadMore} disabled={isFetching}>
                            Charger plus de réponses
                        </BasicButton>
                    ) : null}
                    {replyOpened ? (
                        <div
                            className={classNames([
                                styles.form,
                                {
                                    [styles.withReplies]: (items || []).length > 0,
                                },
                            ])}
                        >
                            <CommentForm
                                commentableKey={commentableKey}
                                parent={id}
                                focused
                                onComplete={onReplyComplete}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
            {loginModalOpened ? (
                <IdentifyModal
                    title="Pour commenter..."
                    source={`comments:${commentableKey}`}
                    description="Vous devez avoir un compte"
                    onClosed={onLoginModalClosed}
                />
            ) : null}
        </div>
    );
}

export default CommentCard;
